import React from 'react';
import { ArrowRight, LogIn, User, UserPlus } from 'lucide-react';

/**
 * First checkout step for buyers who are not signed in: two equally clear options
 * (guest / sign in) plus a secondary "create account" link. Mobile-first, full-width cards.
 */
function ChoiceCard({ icon: Icon, title, hint, badge, onClick, primary = false }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`group w-full min-h-[76px] rounded-2xl border-2 px-4 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
        primary ? 'border-ink bg-white' : 'border-divider bg-white hover:border-ink'
      }`}
    >
      <div className='flex items-center gap-3'>
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
            primary ? 'bg-ink text-white' : 'bg-surface text-ink'
          }`}
        >
          <Icon className='h-5 w-5' />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <p className='text-base font-semibold text-ink'>{title}</p>
            {badge && (
              <span className='rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700'>
                {badge}
              </span>
            )}
          </div>
          <p className='mt-0.5 text-sm text-zinc-500'>{hint}</p>
        </div>
        <ArrowRight className='h-5 w-5 shrink-0 text-zinc-400 transition-transform group-hover:translate-x-0.5' />
      </div>
    </button>
  );
}

export default function AccountChoice({ onGuest, onSignIn, onSignUp, t }) {
  return (
    <div className='space-y-3 py-1'>
      <div className='mb-4'>
        <p className='text-lg font-semibold text-ink'>
          {t('checkout.howToContinue', '¿Cómo querés continuar?')}
        </p>
        <p className='text-sm text-zinc-500'>
          {t('checkout.howToContinueHint', 'No necesitás una cuenta para comprar.')}
        </p>
      </div>

      <ChoiceCard
        primary
        icon={User}
        title={t('checkout.continueAsGuest', 'Continuar como invitado')}
        hint={t('checkout.guestHint', 'Recibirás tu comprobante por correo electrónico')}
        badge={t('checkout.fastest', 'Más rápido')}
        onClick={onGuest}
      />

      <ChoiceCard
        icon={LogIn}
        title={t('checkout.signInWithAccount', 'Ingresar con mi cuenta')}
        hint={t('checkout.signInHint', 'Tus datos se cargan automáticamente')}
        onClick={onSignIn}
      />

      {onSignUp && (
        <p className='pt-2 text-center text-sm text-zinc-500'>
          {t('checkout.noAccount', '¿No tenés cuenta?')}{' '}
          <button
            type='button'
            onClick={onSignUp}
            className='inline-flex items-center gap-1 font-semibold text-ink underline underline-offset-2'
          >
            <UserPlus className='h-3.5 w-3.5' />
            {t('checkout.createAccount', 'Crear cuenta')}
          </button>
        </p>
      )}
    </div>
  );
}
